import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import translations from "./translations";

const LanguageContext = createContext({
  language: "en",
  direction: "ltr",
  setLanguage: () => {},
  toggleLanguage: () => {},
  t: (key) => key,
});

const resolveTranslation = (
  translationObject,
  key
) =>
  key.split(".").reduce(
    (currentValue, keyPart) =>
      currentValue &&
      currentValue[keyPart],
    translationObject
  );

const interpolate = (value, variables) =>
  value.replace(
    /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g,
    (match, variableName) =>
      Object.prototype.hasOwnProperty.call(
        variables,
        variableName
      )
        ? String(variables[variableName])
        : match
  );

export const LanguageProvider = ({
  children,
}) => {
  const [language, setLanguageState] =
    useState(() =>
      localStorage.getItem(
        "ossLanguage"
      ) === "ar"
        ? "ar"
        : "en"
    );

  const direction =
    language === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    localStorage.setItem(
      "ossLanguage",
      language
    );

    document.documentElement.setAttribute(
      "lang",
      language
    );
    document.documentElement.setAttribute(
      "dir",
      direction
    );

    document.body.setAttribute(
      "dir",
      direction
    );
  }, [language, direction]);

  const setLanguage = useCallback(
    (nextLanguage) => {
      setLanguageState(
        nextLanguage === "ar"
          ? "ar"
          : "en"
      );
    },
    []
  );

  const toggleLanguage = useCallback(
    () => {
      setLanguageState(
        (currentLanguage) =>
          currentLanguage === "en"
            ? "ar"
            : "en"
      );
    },
    []
  );

  const t = useCallback(
    (key, variables = {}) => {
      const selectedValue =
        resolveTranslation(
          translations[language],
          key
        );

      const fallbackValue =
        resolveTranslation(
          translations.en,
          key
        );

      const value =
        typeof selectedValue === "string"
          ? selectedValue
          : fallbackValue;

      if (typeof value !== "string") {
        return key;
      }

      return interpolate(
        value,
        variables
      );
    },
    [language]
  );

  const contextValue = useMemo(
    () => ({
      language,
      direction,
      setLanguage,
      toggleLanguage,
      t,
    }),
    [
      language,
      direction,
      setLanguage,
      toggleLanguage,
      t,
    ]
  );

  return (
    <LanguageContext.Provider
      value={contextValue}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
